import { useState, useRef, useCallback, DragEvent, ChangeEvent } from "react";

export interface AttachmentItem {
  id: string;
  name: string;
  size: number;
  file: File;
  status: "pending" | "ready" | "error";
  progress: number;
  warning?: string;
  error?: string;
}

const ALLOWED_EXTENSIONS = [
  ".txt",
  ".md",
  ".markdown",
  ".json",
  ".csv",
  ".pdf",
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".svg",
];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export function useAttachments() {
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const liveRegionRef = useRef<HTMLDivElement>(null);

  const readyAttachments = attachments.filter((a) => a.status === "ready");

  const validateFile = (
    file: File
  ): { valid: boolean; error?: string; warning?: string } => {
    const ext = `.${file.name.split(".").pop()?.toLowerCase()}`;
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return { valid: false, error: `Unsupported file type: ${ext}` };
    }
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: `File too large (max 10MB)` };
    }
    if (file.size === 0) {
      return { valid: false, error: "File is empty" };
    }
    return { valid: true };
  };

  const processFile = useCallback(
    async (file: File): Promise<AttachmentItem> => {
      const validation = validateFile(file);
      const id = `${file.name}-${Date.now()}-${Math.random()}`;

      if (!validation.valid) {
        return {
          id,
          name: file.name,
          size: file.size,
          file,
          status: "error",
          progress: 0,
          error: validation.error,
        };
      }

      // Simulate processing
      const item: AttachmentItem = {
        id,
        name: file.name,
        size: file.size,
        file,
        status: "pending",
        progress: 0,
        warning: validation.warning,
      };

      // Simulate progress
      setTimeout(() => {
        setAttachments((prev) =>
          prev.map((a) => (a.id === id ? { ...a, progress: 50 } : a))
        );
      }, 100);

      setTimeout(() => {
        setAttachments((prev) =>
          prev.map((a) =>
            a.id === id ? { ...a, status: "ready", progress: 100 } : a
          )
        );
      }, 300);

      return item;
    },
    []
  );

  const addFiles = useCallback(
    async (files: File[]) => {
      const newAttachments = await Promise.all(files.map(processFile));
      setAttachments((prev) => [...prev, ...newAttachments]);

      if (liveRegionRef.current) {
        liveRegionRef.current.textContent = `${files.length} file(s) attached`;
      }
    },
    [processFile]
  );

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
    if (liveRegionRef.current) {
      liveRegionRef.current.textContent = "Attachment removed";
    }
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachments([]);
  }, []);

  const handleFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);
      if (files.length > 0) {
        addFiles(files);
      }
      event.target.value = ""; // Reset input
    },
    [addFiles]
  );

  const handleDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragActive(false);

      const files = Array.from(event.dataTransfer?.files || []);
      if (files.length > 0) {
        addFiles(files);
      }
    },
    [addFiles]
  );

  return {
    attachments,
    readyAttachments,
    isDragActive,
    liveRegionRef,
    addFiles,
    removeAttachment,
    clearAttachments,
    handleFileInputChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  };
}
